"use client";

import React, { useEffect, useRef } from "react";
import { Button, CircleIconButton, Heading } from "@pamperedchef/design-system";

import { useClickOutside } from "@/app/hooks/useClickOutside";

import * as styles from "./AddToCartSlider.module.scss";

export interface AddToCartSliderProps extends React.HTMLAttributes<HTMLDivElement> {
	isOpen: boolean;
	onClose: () => void;
	onViewCartAndCheckout: () => void;
	onContinueShopping: () => void;
	children?: React.ReactNode;
}

export function AddToCartSlider({
	isOpen,
	onClose,
	onViewCartAndCheckout,
	onContinueShopping,
	children,
	...props
}: AddToCartSliderProps): JSX.Element {
	const panelRef = useRef<HTMLDivElement | null>(null);

	useClickOutside(panelRef, () => {
		if (isOpen) onClose();
	});

	useEffect(() => {
		if (!isOpen) return;

		function onKeyDown(e: KeyboardEvent): void {
			if (e.key === "Escape") onClose();
		}

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [isOpen, onClose]);

	return isOpen ? (
		<div className={styles.overlay} aria-hidden="true">
			<div
				{...props}
				ref={panelRef}
				role="dialog"
				aria-modal="true"
				aria-label="Item Added to Cart"
				className={styles.panel}
			>
				<div className={styles.header}>
					<CircleIconButton
						className={styles.closeButton}
						icon={{ name: "X" }}
						onClick={onClose}
						aria-label="Close"
					>
						Close
					</CircleIconButton>
					<Heading tag="h2" styledAs="h2" className={`mb-2`}>
						Item Added to Cart
					</Heading>
				</div>

				<div className={styles.content}>{children}</div>

				<div className={styles.footer}>
					<Button
						kind="primary"
						size="lg"
						onClick={onViewCartAndCheckout}
					>
						View Cart & Checkout
					</Button>
					<Button
						kind="secondary"
						size="lg"
						onClick={onContinueShopping}
					>
						Continue Shopping
					</Button>
				</div>
			</div>
		</div>
	) : (
		<></>
	);
}
